import { SafePromise, safeMap } from '@benzinga/safe-await';

import { RestfulClient } from '@benzinga/session';
import { Watchlist, WatchlistSymbol } from '../entities';
import { Session } from '@benzinga/session';

import {
  IncomingWatchlist,
  IncomingWatchlistResult,
  IncomingWatchlistSymbolResult,
  IncomingWatchlistSymbolsResult,
} from '../ingest';
import { WatchlistEnvironment } from '../environment';
import { egressAddTickerToWatchlist, EgressWatchlist, EgressWatchlistSymbol } from '../egress';

export class WatchlistRestful extends RestfulClient {
  constructor(session: Session) {
    super(session.getEnvironment(WatchlistEnvironment).url, session, { 'x-device-key': false });
  }

  removeWatchlist = async (watchlist: Watchlist): SafePromise<undefined> => {
    const url = this.URL(`api/v2/watchlists/${watchlist.watchlistId}`);
    return safeMap(this.delete(url, { credentials: 'include' }), () => undefined);
  };

  getWatchlists = (): SafePromise<IncomingWatchlistResult> => {
    const url = this.URL('api/v2/watchlists/list');
    return this.debouncedGet(url, { credentials: 'include' });
  };

  getYodleeAccess = async (): SafePromise<{ data: { accessToken: string } }> => {
    const url = this.URL('api/v2/watchlists/yodlee/auth');
    return this.post(url, { credentials: 'include' });
  };

  syncYodleeWatchlists = async (): SafePromise<undefined> => {
    const url = this.URL('api/v2/watchlists/yodlee/sync');
    return this.post(url, { credentials: 'include' });
  };

  createWatchlist = async (
    watchlistName: string,
    settings: EgressWatchlist,
    symbols: EgressWatchlistSymbol[],
  ): SafePromise<IncomingWatchlist> => {
    const url = this.URL('api/v2/watchlists/portfolio');
    return this.post(
      url,
      {
        ...settings,
        alerts: symbols,
        portfolio_name: watchlistName,
        status: true,
      },
      { credentials: 'include' },
    );
  };

  updateWatchlistSettings = (watchlist: EgressWatchlist): SafePromise<IncomingWatchlistResult> => {
    const url = this.URL(`api/v2/watchlists/portfolio/${watchlist.portfolio_id}`);
    return this.put(url, watchlist, { credentials: 'include' });
  };

  addTickersToWatchlist = (
    payload: ReturnType<typeof egressAddTickerToWatchlist>,
  ): SafePromise<IncomingWatchlistSymbolsResult> => {
    const url = this.URL('api/v2/watchlists/alert');
    return this.post(url, payload, { credentials: 'include' });
  };

  updateWatchlistTicker = (watchlistSymbol: EgressWatchlistSymbol): SafePromise<IncomingWatchlistSymbolResult> => {
    const url = this.URL(`api/v2/watchlists/alert/${watchlistSymbol.alert_id}`);
    return this.patch(url, watchlistSymbol, { credentials: 'include' });
  };

  removeTickerFromWatchlist = async (watchlist: Watchlist, alert: WatchlistSymbol): SafePromise<undefined> => {
    const url = this.URL(`api/v2/watchlists/alert/${alert.symbolId}`, {
      portfolio_id: watchlist.watchlistId,
    });
    return safeMap(this.delete(url, { credentials: 'include' }), () => undefined);
  };
}
